import React, { useEffect, useRef, useState, useCallback } from 'react';
import Chart from 'chart.js/auto';
import type { GHGInventoryResult } from './GHGInventorySupabase';
import { fetchSectorGhgPerCapitaVsCalifornia } from '../../services/sectorGhgPerCapitaComparisonService';
import { GHG_INVENTORY_CHART_HEIGHT_PX } from '../../theme/ghgInventoryChartLayout';
import { downloadChartJsInstanceAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';

interface SectorGhgPerCapitaVsCaliforniaChartProps {
  location: string;
  ghgData: GHGInventoryResult | null;
  onDownloadPNGReady?: (handler: () => void) => void;
  onDownloadCSVReady?: (handler: () => void) => void;
}

/**
 * Line chart: local total sector-based GHG ÷ population vs California statewide total ÷ population.
 */
export const SectorGhgPerCapitaVsCaliforniaChart: React.FC<SectorGhgPerCapitaVsCaliforniaChartProps> = ({
  location,
  ghgData,
  onDownloadPNGReady,
  onDownloadCSVReady,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const exportRowsRef = useRef<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [skipMessage, setSkipMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!ghgData?.chartData) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      setSkipMessage(null);

      try {
        const { points, skip, message } = await fetchSectorGhgPerCapitaVsCalifornia(location, ghgData.chartData);
        if (cancelled) return;
        if (skip) {
          setSkipMessage(message ?? 'Chart not shown for this selection.');
          setLoading(false);
          return;
        }

        const labels = points.map((p) => String(p.year));
        const localSeries = points.map((p) => (p.localPerCapitaMt != null ? Number(p.localPerCapitaMt.toFixed(4)) : null));
        const caSeries = points.map((p) =>
          p.californiaPerCapitaMt != null ? Number(p.californiaPerCapitaMt.toFixed(4)) : null
        );

        exportRowsRef.current = points.map((p) => ({
          location,
          year: p.year,
          local_per_capita_mt_co2e: p.localPerCapitaMt ?? null,
          california_per_capita_mt_co2e: p.californiaPerCapitaMt ?? null,
        }));

        if (!canvasRef.current) {
          setLoading(false);
          return;
        }

        if (chartRef.current) {
          chartRef.current.destroy();
          chartRef.current = null;
        }

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) {
          setError('Could not get canvas context.');
          setLoading(false);
          return;
        }

        chartRef.current = new Chart(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [
              {
                label: `${location} (MT CO₂e / person)`,
                data: localSeries,
                borderColor: '#1d4ed8',
                backgroundColor: 'rgba(29, 78, 216, 0.08)',
                tension: 0.2,
                spanGaps: false,
                fill: false,
                pointRadius: 3,
                pointHoverRadius: 5,
              },
              {
                label: 'California average (MT CO₂e / person)',
                data: caSeries,
                borderColor: '#0f766e',
                backgroundColor: 'rgba(15, 118, 110, 0.08)',
                tension: 0.2,
                spanGaps: false,
                fill: false,
                pointRadius: 3,
                pointHoverRadius: 5,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { position: 'top' },
              title: {
                display: true,
                text: `Per capita sector GHG vs. California average - ${location}`,
                font: { size: 15, weight: 600 },
              },
              tooltip: {
                callbacks: {
                  label(ctx) {
                    const v = ctx.parsed.y;
                    if (v == null || Number.isNaN(v)) return `${ctx.dataset.label}: —`;
                    return `${ctx.dataset.label}: ${v.toFixed(3)} MT CO₂e / person`;
                  },
                },
              },
            },
            scales: {
              x: {
                title: { display: true, text: 'Year' },
              },
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Metric tons CO₂e per person',
                },
                ticks: {
                  callback: (value) => Number(value).toFixed(2),
                },
              },
            },
          },
        });
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load per-capita comparison.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [location, ghgData]);

  const handleDownloadPNG = useCallback(() => {
    downloadChartJsInstanceAsPNG(
      chartRef.current,
      formatFilename(location, 'Sector_Based_GHG_Per_Capita_vs_CA')
    );
  }, [location]);

  const handleDownloadCSV = useCallback(() => {
    if (!exportRowsRef.current.length) return;
    downloadDataAsCSV(
      exportRowsRef.current,
      formatFilename(location, 'Sector_Based_GHG_Per_Capita_vs_CA')
    );
  }, [location]);

  useEffect(() => {
    onDownloadPNGReady?.(handleDownloadPNG);
  }, [onDownloadPNGReady, handleDownloadPNG]);

  useEffect(() => {
    onDownloadCSVReady?.(handleDownloadCSV);
  }, [onDownloadCSVReady, handleDownloadCSV]);

  if (!ghgData?.chartData) return null;

  if (skipMessage) {
    return (
      <div
        style={{
          marginTop: '2rem',
          padding: '1rem 1.25rem',
          borderRadius: 8,
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          color: '#64748b',
          fontSize: '0.95rem',
        }}
      >
        {skipMessage}
      </div>
    );
  }

  return (
    <div style={{ marginTop: '2rem' }}>
      {loading && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading per-capita comparison…</div>
      )}
      {error && (
        <div style={{ padding: '1rem', color: '#b91c1c', background: '#fef2f2', borderRadius: 8 }}>{error}</div>
      )}
      <div
        style={{
          position: 'relative',
          height: GHG_INVENTORY_CHART_HEIGHT_PX,
          minHeight: GHG_INVENTORY_CHART_HEIGHT_PX,
          display: loading ? 'none' : 'block',
        }}
      >
        <canvas ref={canvasRef} aria-label="Per capita sector GHG comparison chart" />
      </div>
    </div>
  );
};
